import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CONFIG from './config';
import {
  FaCloud,
  FaEdit,
  FaFileUpload,
  FaLink,
  FaCodeBranch,
  FaFolder,
  FaPalette,
  FaPen,
  FaChevronDown
} from 'react-icons/fa';

function RenderPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  // Supabase 세션 확인 → 이미 로그인되어 있으면 /main으로
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${CONFIG.CLOUD_FUNCTION_URL}/auth/me`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.authenticated) {
          navigate('/main', { replace: true });
        } else {
          setIsCheckingAuth(false);
        }
      } catch {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // 스타일 정의
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#fefefe',
      color: '#0f172a',
      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '14px 40px',
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    logo: {
      height: '50px',
      width: 'auto'
    },
    loginButton: {
      backgroundColor: '#4f46e5',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    heroSection: {
      textAlign: 'center',
      padding: '80px 40px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    heroTitle: {
      fontSize: 'clamp(32px, 5vw, 48px)',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#0f172a'
    },
    heroSubtitle: {
      fontSize: 'clamp(16px, 2.5vw, 20px)',
      color: '#64748b',
      lineHeight: '1.6',
      maxWidth: '800px',
      margin: '0 auto 32px'
    },
    section: {
      padding: '60px 40px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    sectionTitle: {
      fontSize: 'clamp(28px, 4vw, 36px)',
      fontWeight: '700',
      marginBottom: '16px',
      textAlign: 'center',
      color: '#0f172a'
    },
    sectionSubtitle: {
      fontSize: 'clamp(14px, 2vw, 18px)',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: '60px',
      maxWidth: '800px',
      margin: '0 auto 60px'
    },
    roadmapPhase: {
      marginBottom: '48px'
    },
    phaseTitle: {
      fontSize: 'clamp(20px, 3vw, 28px)',
      fontWeight: '700',
      marginBottom: '24px',
      color: '#4f46e5'
    },
    timelineCard: {
      background: '#ffffff',
      borderLeft: '3px solid #4f46e5',
      borderRadius: '12px',
      padding: '14px 24px',
      marginBottom: '16px',
      marginLeft: '24px',
      position: 'relative',
      transition: 'transform 0.3s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    timelineDot: {
      position: 'absolute',
      left: '-8px',
      top: '18px',
      width: '13px',
      height: '13px',
      borderRadius: '50%',
      background: '#4f46e5'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px'
    },
    cardIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      background: 'rgba(79, 70, 229, 0.1)',
      color: '#4f46e5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      fontSize: '20px',
      flexShrink: 0
    },
    cardTitle: {
      fontSize: 'clamp(16px, 2vw, 20px)',
      fontWeight: '700',
      marginBottom: '8px',
      color: '#0f172a'
    },
    cardDesc: {
      color: '#64748b',
      lineHeight: '1.6',
      fontSize: 'clamp(13px, 1.5vw, 16px)'
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '32px',
      marginTop: '40px'
    },
    featureItem: {
      padding: '32px',
      borderRadius: '12px',
      background: '#ffffff',
      position: 'relative',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #f0f0f0'
    },
    featureIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'rgba(79, 70, 229, 0.1)',
      color: '#4f46e5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '24px',
      transition: 'all 0.3s ease',
      fontSize: '24px'
    },
    featureTitle: {
      fontWeight: '700',
      marginBottom: '12px',
      fontSize: 'clamp(16px, 2vw, 18px)',
      color: '#0f172a'
    },
    featureDesc: {
      fontSize: 'clamp(13px, 1.5vw, 14px)',
      color: '#64748b',
      lineHeight: '1.6'
    },
    faqContainer: {
      maxWidth: '800px',
      margin: '0 auto'
    },
    faqItem: {
      background: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #f0f0f0',
      cursor: 'pointer'
    },
    faqQuestion: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontWeight: '600',
      color: '#0f172a'
    },
    faqAnswer: {
      marginTop: '16px',
      color: '#64748b',
      lineHeight: '1.6'
    },
    footer: {
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      padding: '32px 40px 30px',
      marginTop: '80px'
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '40px',
      marginBottom: '40px'
    },
    footerColumn: {
      display: 'flex',
      flexDirection: 'column'
    },
    footerTitle: {
      fontSize: '18px',
      fontWeight: '700',
      marginBottom: '16px',
      color: '#ffffff'
    },
    footerTagline: {
      fontSize: '14px',
      color: '#9ca3af',
      lineHeight: '1.6'
    },
    footerLink: {
      fontSize: '14px',
      color: '#9ca3af',
      textDecoration: 'none',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'color 0.2s ease'
    },
    footerText: {
      fontSize: '13px',
      color: '#9ca3af',
      lineHeight: '1.8'
    },
    footerBottom: {
      maxWidth: '1200px',
      margin: '0 auto',
      paddingTop: '30px',
      borderTop: '1px solid #333',
      textAlign: 'center',
      color: '#9ca3af',
      fontSize: '14px'
    }
  };

  // 기능 카드에 그라디언트 보더 추가
  const renderFeatureBorder = () => (
    <div style={{
      content: '""',
      position: 'absolute',
      inset: '0',
      borderRadius: '12px',
      padding: '2px',
      background: 'linear-gradient(45deg, #4f46e5, #06b6d4)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none'
    }} />
  );

  // FAQ 토글
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // FAQ 데이터 - 다국어 지원을 위해 t() 함수 사용
  const faqs = [
    {
      question: t('landing.faq.q1.question'),
      answer: t('landing.faq.q1.answer')
    },
    {
      question: t('landing.faq.q2.question'),
      answer: t('landing.faq.q2.answer')
    },
    {
      question: t('landing.faq.q3.question'),
      answer: t('landing.faq.q3.answer')
    },
    {
      question: t('landing.faq.q4.question'),
      answer: t('landing.faq.q4.answer')
    },
    {
      question: t('landing.faq.q5.question'),
      answer: t('landing.faq.q5.answer')
    },
    {
      question: t('landing.faq.q6.question'),
      answer: t('landing.faq.q6.answer')
    },
    {
      question: t('landing.faq.q7.question'),
      answer: t('landing.faq.q7.answer')
    }
  ];

  // 인증 상태 확인 중이면 로딩 표시
  if (isCheckingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fefefe'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #1A62EA',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `
          }} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <img src="/ddalkkakcheck_text.png" alt="딸깍체크 로고" style={styles.logo} />
        <button
          style={styles.loginButton}
          onClick={() => navigate('/login')}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4338ca'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#4f46e5'}
        >
          {t('landing.login')}
        </button>
      </header>

      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <h1 style={{ ...styles.heroTitle, position: 'relative', zIndex: 2 }}>
            {t('landing.hero.title')}
          </h1>
          <img
            src="/ddalkkak2.png"
            alt="딸깍"
            style={{
              position: 'absolute',
              right: '-15vw',
              top: '-7vw',
              width: '25vw',
              height: 'auto',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: 0.8
            }}
          />
        </div>
        <p style={{ ...styles.heroSubtitle, position: 'relative', zIndex: 2 }}>
          {t('landing.hero.subtitle')}
        </p>
        <button
          style={{
            ...styles.loginButton,
            padding: '14px 48px',
            fontSize: '16px',
            boxShadow: '0 8px 24px rgba(79, 70, 229, 0.2)',
            position: 'relative',
            zIndex: 2
          }}
          onClick={() => navigate('/login')}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4338ca';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 12px 32px rgba(79, 70, 229, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#4f46e5';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 8px 24px rgba(79, 70, 229, 0.2)';
          }}
        >
          {t('landing.hero.cta')}
        </button>
      </section>

      {/* Available Features Section - 위로 이동 */}
      <section style={{ ...styles.section, marginTop: '-80px' }}>
        <h2 style={styles.sectionTitle}>
          <span style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {t('landing.features.badge')}
          </span> {t('landing.features.badgeSub')}
        </h2>
        <p style={styles.sectionSubtitle}>
          {t('landing.features.title')}
        </p>

        <div style={styles.featureGrid}>
          {[
            { icon: <FaEdit />, title: t('landing.features.typo.title'), desc: t('landing.features.typo.desc') },
            { icon: <FaCloud />, title: t('landing.features.translate.title'), desc: t('landing.features.translate.desc') },
            { icon: <FaPalette />, title: t('landing.features.color.title'), desc: t('landing.features.color.desc') },
            { icon: <FaPen />, title: t('landing.features.font.title'), desc: t('landing.features.font.desc') },
            { icon: <FaFolder />, title: t('landing.features.brandKit.title'), desc: t('landing.features.brandKit.desc') }
          ].map((feature, index) => (
            <div
              key={index}
              style={styles.featureItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                const icon = e.currentTarget.querySelector('.feature-icon');
                if (icon) {
                  icon.style.background = '#4f46e5';
                  icon.style.color = 'white';
                  icon.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                const icon = e.currentTarget.querySelector('.feature-icon');
                if (icon) {
                  icon.style.background = 'rgba(79, 70, 229, 0.1)';
                  icon.style.color = '#4f46e5';
                  icon.style.transform = 'scale(1)';
                }
              }}
            >
              {renderFeatureBorder()}
              <div className="feature-icon" style={styles.featureIcon}>
                {feature.icon}
              </div>
              <h3 style={styles.featureTitle}>{feature.title}</h3>
              <p style={styles.featureDesc}>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap Section */}
      <section style={{ ...styles.section, marginTop: '-40px' }}>
        <h2 style={styles.sectionTitle}>{t('landing.roadmap.title')}</h2>
        <p style={styles.sectionSubtitle}>
          {t('landing.roadmap.subtitle')}
        </p>

        {/* Phase 1: 현재 기능 */}
        <div style={styles.roadmapPhase}>
          <h3 style={styles.phaseTitle}>{t('landing.roadmap.phase1.title')}</h3>

          {[
            { icon: <FaEdit />, title: t('landing.roadmap.phase1.typo.title'), desc: t('landing.roadmap.phase1.typo.desc') },
            { icon: <FaCloud />, title: t('landing.roadmap.phase1.translate.title'), desc: t('landing.roadmap.phase1.translate.desc') },
            { icon: <FaPalette />, title: t('landing.roadmap.phase1.color.title'), desc: t('landing.roadmap.phase1.color.desc') },
            { icon: <FaPen />, title: t('landing.roadmap.phase1.font.title'), desc: t('landing.roadmap.phase1.font.desc') }
          ].map((item, index) => (
            <div
              key={index}
              style={styles.timelineCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(10px)';
                const icon = e.currentTarget.querySelector('.card-icon');
                if (icon) {
                  icon.style.background = '#4f46e5';
                  icon.style.color = 'white';
                  icon.style.transform = 'rotate(360deg)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                const icon = e.currentTarget.querySelector('.card-icon');
                if (icon) {
                  icon.style.background = 'rgba(79, 70, 229, 0.1)';
                  icon.style.color = '#4f46e5';
                  icon.style.transform = 'rotate(0deg)';
                }
              }}
            >
              <div style={styles.timelineDot}></div>
              <div style={styles.cardHeader}>
                <div className="card-icon" style={styles.cardIcon}>
                  {item.icon}
                </div>
                <div>
                  <h4 style={styles.cardTitle}>{item.title}</h4>
                  <p style={styles.cardDesc}>{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Phase 2: 향후 계획 */}
        <div style={styles.roadmapPhase}>
          <h3 style={styles.phaseTitle}>{t('landing.roadmap.phase2.title')}</h3>

          {[
            { icon: <FaLink />, title: t('landing.roadmap.phase2.autoFix.title'), desc: t('landing.roadmap.phase2.autoFix.desc') },
            { icon: <FaCodeBranch />, title: t('landing.roadmap.phase2.teamwork.title'), desc: t('landing.roadmap.phase2.teamwork.desc') },
            { icon: <FaFileUpload />, title: t('landing.roadmap.phase2.api.title'), desc: t('landing.roadmap.phase2.api.desc') }
          ].map((item, index) => (
            <div
              key={index}
              style={styles.timelineCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(10px)';
                const icon = e.currentTarget.querySelector('.card-icon');
                if (icon) {
                  icon.style.background = '#4f46e5';
                  icon.style.color = 'white';
                  icon.style.transform = 'rotate(360deg)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                const icon = e.currentTarget.querySelector('.card-icon');
                if (icon) {
                  icon.style.background = 'rgba(79, 70, 229, 0.1)';
                  icon.style.color = '#4f46e5';
                  icon.style.transform = 'rotate(0deg)';
                }
              }}
            >
              <div style={styles.timelineDot}></div>
              <div style={styles.cardHeader}>
                <div className="card-icon" style={styles.cardIcon}>
                  {item.icon}
                </div>
                <div>
                  <h4 style={styles.cardTitle}>{item.title}</h4>
                  <p style={styles.cardDesc}>{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ ...styles.section, marginTop: '-100px' }}>
        <div style={styles.faqContainer}>
          <h2 style={{ ...styles.sectionTitle, marginBottom: '48px' }}>{t('landing.faq.title')}</h2>

          {(showAllFaqs ? faqs : faqs.slice(0, 3)).map((faq, index) => (
            <div
              key={index}
              style={styles.faqItem}
              onClick={() => toggleFaq(index)}
            >
              <div style={styles.faqQuestion}>
                <span>{faq.question}</span>
                <FaChevronDown
                  style={{
                    transition: 'transform 0.3s ease',
                    transform: openFaqIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </div>
              {openFaqIndex === index && (
                <div style={styles.faqAnswer}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}

          {faqs.length > 3 && (
            <button
              onClick={() => setShowAllFaqs(!showAllFaqs)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '16px 24px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                color: '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              <FaChevronDown
                style={{
                  transition: 'transform 0.3s ease',
                  transform: showAllFaqs ? 'rotate(180deg)' : 'rotate(0deg)',
                  fontSize: '16px'
                }}
              />
            </button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          {/* Column 1: 브랜드 */}
          <div style={styles.footerColumn}>
            <h3 style={styles.footerTitle}>{t('landing.footer.brand')}</h3>
            <p style={styles.footerTagline}>{t('landing.footer.tagline')}</p>
          </div>

          {/* Column 2: 서비스 */}
          <div style={styles.footerColumn}>
            <h3 style={styles.footerTitle}>{t('landing.footer.services')}</h3>
            <span
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.typoCheck')}
            </span>
            <span
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.colorCheck')}
            </span>
            <span
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.fontCheck')}
            </span>
            <span
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.translate')}
            </span>
          </div>

          {/* Column 3: 법적 고지 */}
          <div style={styles.footerColumn}>
            <h3 style={styles.footerTitle}>{t('landing.footer.legal')}</h3>
            <a
              href="https://doc.xn--il0b54k.net/ko/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.footerLink, textDecoration: 'none' }}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.terms')}
            </a>
            <a
              href="https://doc.xn--il0b54k.net/ko/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.footerLink, textDecoration: 'none' }}
              onMouseEnter={(e) => e.target.style.color = '#ffffff'}
              onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
            >
              {t('landing.footer.privacy')}
            </a>
          </div>

          {/* Column 4: 회사 정보 */}
          <div style={styles.footerColumn}>
            <h3 style={styles.footerTitle}>{t('landing.footer.companyInfo')}</h3>
            <p style={styles.footerText}>
              {t('landing.footer.companyName')}<br />
              {t('landing.footer.ceo')}<br />
              {t('landing.footer.businessNumber')}<br />
              {t('landing.footer.address')}<br />
              {t('landing.footer.email')}
            </p>
          </div>
        </div>

        {/* Footer Bottom */}
        <div style={styles.footerBottom}>
          <p>&copy; 2026 {t('landing.footer.copyright')}</p>
        </div>
      </footer>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .feature-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 900px) {
          footer > div:first-child {
            grid-template-columns: 1fr 1fr !important;
            gap: 32px !important;
          }
        }
        @media (max-width: 600px) {
          footer > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default RenderPage;
